<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Loading -->
    <div v-if="loading" class="flex-1 flex items-center justify-center p-12">
      <LoadingIndicator message="Loading population data..." />
    </div>

    <!-- Error -->
    <div v-else-if="error" class="p-5">
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <p class="text-red-700">{{ error }}</p>
        <button @click="fetchAll" class="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    </div>

    <!-- Loaded -->
    <template v-else-if="assessmentData">
      <!-- Summary Stats Strip -->
      <div class="px-5 pt-5">
        <div class="grid grid-cols-5 gap-4 mb-4">
          <div class="bg-gray-50 rounded-lg p-4 text-center">
            <div class="text-2xl font-bold text-gray-900">{{ formatNumber(assessmentData.total_population) }}</div>
            <div class="text-xs text-gray-500 mt-1">Total Population</div>
          </div>
          <div class="bg-gray-50 rounded-lg p-4 text-center">
            <div class="text-2xl font-bold text-gray-900">{{ sdohPct }}%</div>
            <div class="text-xs text-gray-500 mt-1">SDOH Indicators</div>
          </div>
          <div class="bg-gray-50 rounded-lg p-4 text-center">
            <div class="text-2xl font-bold text-gray-900">{{ minorityPct }}%</div>
            <div class="text-xs text-gray-500 mt-1">Racial/Ethnic Minorities</div>
          </div>
          <div class="bg-gray-50 rounded-lg p-4 text-center">
            <div class="text-2xl font-bold text-gray-900">{{ lepPct }}%</div>
            <div class="text-xs text-gray-500 mt-1">Limited English Proficiency</div>
          </div>
          <div class="bg-gray-50 rounded-lg p-4 text-center">
            <div class="text-2xl font-bold text-gray-900">{{ childrenPct }}%</div>
            <div class="text-xs text-gray-500 mt-1">Children &amp; Adolescents</div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-end mb-4">
          <button
            @click="$router.push('/phm/assessment-report')"
            class="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm rounded hover:bg-gray-800 transition-colors"
          >
            <font-awesome-icon :icon="['fas', 'file-lines']" />
            Generate Assessment Report
          </button>
        </div>
      </div>

      <!-- Factor Sidebar + Content -->
      <div class="flex flex-1 overflow-hidden border-t border-gray-200">
        <!-- Left: Factor List -->
        <div class="w-64 shrink-0 border-r border-gray-200 overflow-y-auto">
          <div
            v-for="subpop in assessmentData.subpopulations"
            :key="subpop.id"
            class="flex items-center justify-between px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
            :class="selectedFactorId === subpop.id ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'"
            @click="selectFactor(subpop.id)"
          >
            <div class="min-w-0">
              <div class="text-sm font-medium truncate" :class="selectedFactorId === subpop.id ? 'text-white' : 'text-gray-900'">{{ subpop.name }}</div>
              <div class="text-xs" :class="selectedFactorId === subpop.id ? 'text-gray-300' : 'text-gray-400'">Factor {{ subpop.ncqa_factor }}</div>
            </div>
            <div class="text-right shrink-0 ml-3">
              <div class="text-sm font-bold" :class="selectedFactorId === subpop.id ? 'text-white' : 'text-gray-900'">{{ formatNumber(subpop.count) }}</div>
              <div class="text-xs" :class="selectedFactorId === subpop.id ? 'text-gray-300' : 'text-gray-500'">{{ subpop.percentage }}%</div>
            </div>
          </div>

          <!-- NCQA Context -->
          <div class="px-4 py-4 border-t border-gray-200 bg-gray-50">
            <div class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">NCQA Factor</div>
            <p class="text-xs text-gray-500 leading-relaxed">{{ ncqaContext }}</p>
          </div>
        </div>

        <!-- Right: Content with child tabs -->
        <div class="flex-1 flex flex-col overflow-hidden" v-if="selectedFactor">
          <!-- Child tabs -->
          <div class="flex border-b border-gray-200 bg-white px-6 pt-3">
            <button
              v-for="tab in childTabs"
              :key="tab.key"
              @click="childTab = tab.key"
              :class="[
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
                childTab === tab.key
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >{{ tab.label }}</button>
          </div>

          <!-- Summary Tab -->
          <div v-if="childTab === 'summary'" class="flex-1 overflow-y-auto">
            <!-- Factor Header -->
            <div class="px-6 pt-5 pb-4 border-b border-gray-100">
              <div class="flex items-start justify-between">
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">{{ selectedFactor.name }}</h3>
                  <span class="text-sm text-gray-400">NCQA Factor {{ selectedFactor.ncqa_factor }}</span>
                  <p v-if="selectedFactor.description" class="text-sm text-gray-500 mt-1 max-w-prose">{{ selectedFactor.description }}</p>
                </div>
                <div class="text-right">
                  <div class="text-3xl font-bold text-gray-900">{{ formatNumber(selectedFactor.count) }}</div>
                  <div class="text-sm text-gray-500">{{ selectedFactor.percentage }}% of population</div>
                </div>
              </div>
            </div>

            <!-- SDOH Indicators (Factor 1 only) -->
            <div v-if="selectedFactor.sdoh_indicators" class="px-6 py-4 border-b border-gray-100">
              <div class="text-sm font-medium text-blue-700 mb-3">Social Determinants of Health</div>
              <div class="grid grid-cols-2 gap-3">
                <div v-if="selectedFactor.sdoh_indicators.dual_eligible" class="bg-blue-50 rounded-lg p-3">
                  <div class="text-xs text-gray-500">{{ selectedFactor.sdoh_indicators.dual_eligible.label }}</div>
                  <div class="text-lg font-bold text-gray-900 mt-1">{{ formatNumber(selectedFactor.sdoh_indicators.dual_eligible.count) }}</div>
                  <div class="text-xs text-gray-500">{{ selectedFactor.sdoh_indicators.dual_eligible.percentage }}% of population</div>
                  <div v-if="selectedFactor.sdoh_indicators.dual_eligible.benchmark" class="text-xs mt-1" :class="benchmarkClass(selectedFactor.sdoh_indicators.dual_eligible.percentage, selectedFactor.sdoh_indicators.dual_eligible.benchmark.value)">
                    {{ benchmarkLabel(selectedFactor.sdoh_indicators.dual_eligible.percentage, selectedFactor.sdoh_indicators.dual_eligible.benchmark.value) }} national avg ({{ selectedFactor.sdoh_indicators.dual_eligible.benchmark.value }}%, {{ selectedFactor.sdoh_indicators.dual_eligible.benchmark.source }})
                  </div>
                </div>
                <div v-if="selectedFactor.sdoh_indicators.lep" class="bg-blue-50 rounded-lg p-3">
                  <div class="text-xs text-gray-500">{{ selectedFactor.sdoh_indicators.lep.label }}</div>
                  <div class="text-lg font-bold text-gray-900 mt-1">{{ formatNumber(selectedFactor.sdoh_indicators.lep.count) }}</div>
                  <div class="text-xs text-gray-500">{{ selectedFactor.sdoh_indicators.lep.percentage }}% of population</div>
                </div>
                <div v-if="selectedFactor.sdoh_indicators.avg_chronic_conditions" class="bg-blue-50 rounded-lg p-3">
                  <div class="text-xs text-gray-500">Avg Chronic Conditions</div>
                  <div class="text-lg font-bold text-gray-900 mt-1">{{ selectedFactor.sdoh_indicators.avg_chronic_conditions }}</div>
                </div>
              </div>
            </div>

            <!-- Sub-segments Breakdown -->
            <div v-if="selectedFactor.sub_segments" class="px-6 py-4 border-b border-gray-100">
              <div class="text-sm font-medium text-gray-700 mb-3">Breakdown</div>
              <div class="space-y-2.5">
                <div v-for="(seg, key) in selectedFactor.sub_segments" :key="key">
                  <div class="flex items-center justify-between text-sm mb-1">
                    <span class="text-gray-700">{{ seg.label || formatLabel(key) }}</span>
                    <div class="text-right">
                      <span class="font-medium text-gray-900">{{ formatNumber(seg.count) }} <span class="text-gray-400">({{ seg.percentage }}%)</span></span>
                      <span v-if="seg.benchmark" class="ml-2 text-xs" :class="benchmarkClass(seg.population_pct != null ? seg.population_pct : seg.percentage, seg.benchmark.value)">
                        {{ benchmarkLabel(seg.population_pct != null ? seg.population_pct : seg.percentage, seg.benchmark.value) }} {{ seg.benchmark.value }}% natl
                      </span>
                    </div>
                  </div>
                  <div class="w-full bg-gray-100 rounded-full h-1.5">
                    <div class="h-1.5 rounded-full transition-all duration-300"
                      :style="{ width: seg.percentage + '%', backgroundColor: segmentColor(key), minWidth: seg.count > 0 ? '4px' : '0' }">
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Custom Subpopulations (Factor 7) -->
            <div v-if="selectedFactor.subpopulations" class="px-6 py-4 border-b border-gray-100 space-y-4">
              <div v-for="custom in selectedFactor.subpopulations" :key="custom.name" class="border border-gray-100 rounded-lg p-4">
                <div class="flex items-center justify-between mb-3">
                  <span class="text-sm font-semibold text-gray-900">{{ custom.name }}</span>
                  <span class="text-sm text-gray-500">{{ formatNumber(custom.count) }} ({{ custom.percentage }}%)</span>
                </div>
                <div v-if="custom.characteristics" class="grid grid-cols-2 gap-3">
                  <div v-for="(val, charKey) in custom.characteristics" :key="charKey" class="bg-gray-50 rounded p-3">
                    <div class="text-xs text-gray-500">{{ formatLabel(charKey) }}</div>
                    <div class="text-sm font-bold text-gray-900 mt-1">{{ formatCharacteristic(charKey, val) }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- HCC Prevalence Chart -->
            <div v-if="registryLoaded && filteredPatients.length" class="px-6 py-4">
              <SubpopulationInsights
                :key="activeSubpop"
                :subpop="activeSubpop"
                :patients="filteredPatients"
                :all-patients="patients"
                :hcc-labels="hccLabels"
              />
            </div>
          </div>

          <!-- Patient List Tab -->
          <div v-if="childTab === 'patients'" class="flex flex-1 overflow-hidden p-5">
          <div class="flex flex-1 overflow-hidden border border-gray-200 rounded-lg">
            <!-- Table -->
            <div :style="{ width: selectedPatient ? '55%' : '100%' }" class="overflow-y-auto transition-all duration-200">
              <table class="w-full" style="table-layout: auto">
                <thead class="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th v-for="col in columns" :key="col.key"
                      class="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-700"
                      @click="toggleSort(col.key)"
                    >
                      {{ col.label }}
                      <span v-if="sortKey === col.key" class="ml-0.5 text-primary-500">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="pt in paginatedPatients"
                    :key="pt.mbi"
                    :class="['border-b border-gray-100 cursor-pointer transition-colors', selectedPatient?.mbi === pt.mbi ? 'bg-blue-50' : 'hover:bg-gray-50']"
                    @click="selectPatient(pt)"
                  >
                    <td class="px-3 py-2">
                      <div class="sensitive text-sm font-medium text-gray-900">{{ pt.mbi }}</div>
                      <div class="text-xs text-gray-400">Age {{ pt.age ?? '?' }}</div>
                    </td>
                    <td v-if="activeSubpop === 'race'" class="px-3 py-2 text-xs text-gray-700">{{ pt.race_label }}</td>
                    <td v-if="activeSubpop === 'race'" class="px-3 py-2 text-xs text-gray-700">{{ ethnicityLabel(pt) }}</td>
                    <td v-if="activeSubpop === 'lep'" class="px-3 py-2 text-xs text-gray-700">{{ pt.language || '—' }}</td>
                    <td v-if="activeSubpop === 'custom'" class="px-3 py-2 text-xs text-gray-700">{{ pt.full_dual ? 'Full' : pt.partial_dual ? 'Partial' : '—' }}</td>
                    <td v-if="activeSubpop === 'custom'" class="px-3 py-2">
                      <span v-if="pt.high_acuity" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">High</span>
                      <span v-else class="text-xs text-gray-300">—</span>
                    </td>
                    <td class="px-3 py-2">
                      <span v-if="pt.risk_level" :class="riskClass(pt.risk_level)" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium">{{ pt.risk_level }}</span>
                      <span v-else class="text-xs text-gray-300">—</span>
                    </td>
                    <td class="px-3 py-2 text-xs text-gray-700">{{ pt.num_chronic ?? '—' }}</td>
                    <td class="px-3 py-2">
                      <div class="flex gap-1 flex-wrap">
                        <span v-for="hcc in getHCCs(pt).slice(0, 3)" :key="hcc" class="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">{{ hcc }}</span>
                        <span v-if="getHCCs(pt).length > 3" class="text-[10px] text-gray-400">+{{ getHCCs(pt).length - 3 }}</span>
                      </div>
                    </td>
                    <td class="px-3 py-2 relative" @click.stop>
                      <div class="flex flex-wrap gap-1 items-center">
                        <span v-for="prog in (patientPrograms[pt.mbi] || [])" :key="prog.metric__metric_name_slug"
                          class="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary-100 text-primary-700">
                          {{ prog.metric__metric_name_slug }}
                        </span>
                        <button
                          @click.stop="openEnroll(pt)"
                          class="px-1.5 py-0.5 rounded text-[10px] font-medium border border-dashed border-gray-300 text-gray-400 hover:text-primary-600 hover:border-primary-400 transition-colors"
                        >+ Enroll</button>
                      </div>
                      <!-- Enrollment popover -->
                      <div v-if="enrollPopover === pt.mbi" class="absolute z-30 top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-56">
                        <div class="text-xs font-semibold text-gray-700 mb-2">Enroll in Program</div>
                        <div v-for="prog in unenrolledPrograms(pt)" :key="prog.id" class="flex items-center gap-2 py-1">
                          <input type="checkbox" :value="prog.id" v-model="enrollSelections" class="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                          <span class="text-xs text-gray-700">{{ prog.name }}</span>
                        </div>
                        <div v-if="!unenrolledPrograms(pt).length" class="text-xs text-gray-400 py-1">Enrolled in all programs</div>
                        <div class="flex justify-end gap-2 mt-2 pt-2 border-t border-gray-100">
                          <button @click="enrollPopover = null" class="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                          <button @click="submitEnroll(pt)" :disabled="!enrollSelections.length || enrolling"
                            class="px-2 py-1 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-40">
                            {{ enrolling ? '...' : 'Enroll' }}
                          </button>
                        </div>
                      </div>
                    </td>
                    <td class="px-3 py-2 text-xs text-gray-700">{{ pt.num_ed || 0 }}</td>
                    <td class="px-3 py-2 text-xs text-gray-700">{{ pt.num_ip || 0 }}</td>
                    <td class="px-3 py-2">
                      <div class="flex flex-wrap gap-1">
                        <span v-if="pt.disabled" class="px-1 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700">DIS</span>
                        <span v-if="pt.dual" class="px-1 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">DUAL</span>
                        <span v-if="pt.has_smi" class="px-1 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">SMI</span>
                        <span v-if="pt.is_lep" class="px-1 py-0.5 rounded text-[10px] font-medium bg-teal-100 text-teal-700">LEP</span>
                        <span v-if="pt.esrd" class="px-1 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">ESRD</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <!-- Pagination -->
              <div v-if="totalPages > 1" class="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-2 flex items-center justify-between">
                <span class="text-xs text-gray-500">{{ filteredPatients.length.toLocaleString() }} members</span>
                <div class="flex items-center gap-2">
                  <button @click="page = Math.max(1, page - 1)" :disabled="page === 1" class="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40">Prev</button>
                  <span class="text-xs text-gray-600">{{ page }} / {{ totalPages }}</span>
                  <button @click="page = Math.min(totalPages, page + 1)" :disabled="page === totalPages" class="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40">Next</button>
                </div>
              </div>
            </div>
            <!-- Patient Drawer -->
            <div v-if="selectedPatient" style="width: 45%" class="border-l border-gray-200 overflow-hidden">
              <Patient_Drawer
                :key="selectedPatient.mbi"
                :patient-identifier="selectedPatient.mbi"
                :patient-name="selectedPatient.mbi"
                :enabled-tabs="['details', 'utilization', 'programs', 'care_plan', 'care_team']"
                :show-header="true"
                @close="selectedPatient = null"
              />
            </div>
          </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import SubpopulationInsights from '@/components/PopulationRiskAssessment/SubpopulationInsights.vue';

const SEGMENT_COLORS = ['#F76734', '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#6366F1', '#EC4899'];

const FACTOR_TO_SUBPOP = {
  general: 'general',
  children: 'pediatric',
  disabled: 'disabled',
  smi: 'smi',
  race_ethnicity: 'race',
  lep: 'lep',
  custom: 'custom',
};

const NCQA_CONTEXT = {
  general: 'Factor 1: Assess the general population including social determinants of health — housing, food access, transportation, and financial barriers.',
  children: 'Factor 2: Assess the needs of children and adolescents, including developmental screening, immunization status, and behavioral health.',
  disabled: 'Factor 3: Assess members with disabilities including ESRD, functional limitations, and access barriers.',
  smi: 'Factor 4: Assess members with serious mental illness — depression, schizophrenia, bipolar disorder.',
  race_ethnicity: 'Factor 5: Assess needs of racial and ethnic groups, identify disparities in care access and outcomes.',
  lep: 'Factor 6: Assess members with limited English proficiency — language barriers, interpreter needs, culturally appropriate care.',
  custom: 'Factor 7: Custom subpopulations — dual-eligible members and high-acuity complex members with distinct clinical needs.',
};

function parseHCCs(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string') { try { return JSON.parse(raw).map(String); } catch { return []; } }
  return [];
}

export default {
  name: 'PHM_PopulationAssessment',
  components: { LoadingIndicator, Patient_Drawer, SubpopulationInsights },

  data() {
    return {
      loading: false,
      error: null,
      assessmentData: null,
      patients: [],
      hccLabels: {},
      registryLoaded: false,
      selectedFactorId: 'general',
      selectedPatient: null,
      childTab: 'summary',
      sortKey: 'num_chronic',
      sortDir: 'desc',
      page: 1,
      pageSize: 50,
      childTabs: [
        { key: 'summary', label: 'Summary' },
        { key: 'patients', label: 'Patient List' },
      ],
      // Program enrollment
      availablePrograms: [],
      patientPrograms: {},  // entity_id → [{metric_name, metric_name_slug}]
      enrollPopover: null,  // mbi of patient with open popover
      enrollSelections: [],
      enrolling: false,
    };
  },

  computed: {
    selectedFactor() {
      if (!this.assessmentData?.subpopulations) return null;
      return this.assessmentData.subpopulations.find(s => s.id === this.selectedFactorId) || this.assessmentData.subpopulations[0];
    },
    activeSubpop() {
      return FACTOR_TO_SUBPOP[this.selectedFactorId] || 'general';
    },
    ncqaContext() {
      return NCQA_CONTEXT[this.selectedFactorId] || '';
    },
    sdohPct() {
      const g = this.assessmentData?.subpopulations?.find(s => s.id === 'general');
      return g?.sdoh_indicators?.dual_eligible?.percentage || '0';
    },
    minorityPct() {
      const r = this.assessmentData?.subpopulations?.find(s => s.id === 'race_ethnicity');
      if (!r?.sub_segments) return '0';
      return Object.entries(r.sub_segments)
        .filter(([k]) => k !== 'white' && k !== 'unknown')
        .reduce((sum, [, v]) => sum + (v.percentage || 0), 0).toFixed(1);
    },
    lepPct() {
      return this.assessmentData?.subpopulations?.find(s => s.id === 'lep')?.percentage || '0';
    },
    childrenPct() {
      return this.assessmentData?.subpopulations?.find(s => s.id === 'children')?.percentage || '0';
    },
    filteredPatients() {
      const sp = this.activeSubpop;
      if (sp === 'general') return this.patients;
      if (sp === 'custom') return this.patients.filter(p => p.subpops?.dual || p.subpops?.high_acuity);
      return this.patients.filter(p => p.subpops?.[sp]);
    },
    columns() {
      const cols = [{ key: 'member', label: 'Member' }];
      if (this.activeSubpop === 'race') {
        cols.push({ key: 'race', label: 'Race' }, { key: 'ethnicity', label: 'Ethnicity' });
      }
      if (this.activeSubpop === 'lep') {
        cols.push({ key: 'language', label: 'Language' });
      }
      if (this.activeSubpop === 'custom') {
        cols.push({ key: 'dual_type', label: 'Dual Status' }, { key: 'acuity', label: 'Acuity' });
      }
      cols.push(
        { key: 'risk', label: 'Risk' },
        { key: 'chronic', label: 'Chronic' },
        { key: 'hccs', label: 'HCCs' },
        { key: 'programs', label: 'Programs' },
        { key: 'ed', label: 'ED' },
        { key: 'ip', label: 'IP' },
        { key: 'flags', label: 'Flags' },
      );
      return cols;
    },
    sortedPatients() {
      const pts = [...this.filteredPatients];
      const dir = this.sortDir === 'asc' ? 1 : -1;
      pts.sort((a, b) => {
        switch (this.sortKey) {
          case 'member': return dir * (a.mbi || '').localeCompare(b.mbi || '');
          case 'risk': return dir * (({ HIGH: 3, MEDIUM: 2, LOW: 1 }[a.risk_level] || 0) - ({ HIGH: 3, MEDIUM: 2, LOW: 1 }[b.risk_level] || 0));
          case 'chronic': return dir * ((a.num_chronic ?? -1) - (b.num_chronic ?? -1));
          case 'ed': return dir * ((a.num_ed || 0) - (b.num_ed || 0));
          case 'ip': return dir * ((a.num_ip || 0) - (b.num_ip || 0));
          default: return 0;
        }
      });
      return pts;
    },
    totalPages() { return Math.ceil(this.sortedPatients.length / this.pageSize) || 1; },
    paginatedPatients() {
      const start = (this.page - 1) * this.pageSize;
      return this.sortedPatients.slice(start, start + this.pageSize);
    },
  },

  watch: {
    selectedFactorId() {
      this.page = 1;
      this.selectedPatient = null;
      this.childTab = 'summary';
    },
  },

  mounted() {
    this.fetchAll();
    this.fetchPrograms();
    this.fetchAllPatientPrograms();
  },

  methods: {
    async fetchAll() {
      this.loading = true;
      this.error = null;
      try {
        const [assessRes, regRes] = await Promise.all([
          this.$axios.get('/api/phm/population-assessment/'),
          this.$axios.get('/api/phm/population-registry/'),
        ]);
        this.assessmentData = assessRes.data;
        this.patients = regRes.data.patients || [];
        this.hccLabels = regRes.data.hcc_labels || {};
        this.registryLoaded = true;
      } catch (err) {
        this.error = err.response?.data?.detail || 'Failed to load population data';
      } finally {
        this.loading = false;
      }
    },
    async fetchPrograms() {
      try {
        const { data } = await this.$axios.get('/api/phm/programs/');
        this.availablePrograms = data;
      } catch (e) { /* silent */ }
    },
    async fetchAllPatientPrograms() {
      try {
        const { data } = await this.$axios.get('/api/phm/all-patient-programs/');
        this.patientPrograms = data;
      } catch (e) { /* silent */ }
    },
    unenrolledPrograms(pt) {
      const enrolled = (this.patientPrograms[pt.mbi] || []).map(p => p.metric_id);
      return this.availablePrograms.filter(p => !enrolled.includes(p.id));
    },
    openEnroll(pt) {
      this.enrollPopover = this.enrollPopover === pt.mbi ? null : pt.mbi;
      this.enrollSelections = [];
    },
    async submitEnroll(pt) {
      if (!this.enrollSelections.length) return;
      this.enrolling = true;
      try {
        for (const metricId of this.enrollSelections) {
          await this.$axios.post('/api/opportunities/enroll-patient/', {
            patient_identifier: pt.mbi,
            metric_id: metricId,
            entity_id: pt.entity_id || undefined,
            bene_first_name: pt.bene_first_name || undefined,
            bene_last_name: pt.bene_last_name || undefined,
          });
        }
        // Refresh all patient programs
        await this.fetchAllPatientPrograms();
        this.enrollPopover = null;
        this.enrollSelections = [];
      } catch (e) {
        console.error('Enrollment failed:', e);
      } finally {
        this.enrolling = false;
      }
    },
    selectFactor(id) { this.selectedFactorId = id; },
    selectPatient(pt) { this.selectedPatient = this.selectedPatient?.mbi === pt.mbi ? null : pt; },
    toggleSort(key) {
      if (this.sortKey === key) { this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'; }
      else { this.sortKey = key; this.sortDir = 'desc'; }
    },
    getHCCs(pt) { return parseHCCs(pt.hcc_codes); },
    ethnicityLabel(pt) { return String(pt.race_cd) === '5' ? 'Hispanic' : 'Non-Hispanic'; },
    formatNumber(n) { return n != null ? Number(n).toLocaleString() : '0'; },
    formatLabel(key) { return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); },
    formatCharacteristic(key, val) {
      if (typeof val === 'object' && val !== null) {
        if (val.count != null) return this.formatNumber(val.count) + ' (' + val.percentage + '%)';
        return JSON.stringify(val);
      }
      if (key.includes('pct') || key.includes('rate')) return val + '%';
      if (key.includes('avg')) return Number(val).toFixed(1);
      return Number(val).toLocaleString();
    },
    segmentColor(key) {
      const hash = key.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
      return SEGMENT_COLORS[hash % SEGMENT_COLORS.length];
    },
    benchmarkClass(actual, national) {
      if (actual == null || national == null) return 'text-gray-400';
      return actual > national ? 'text-amber-600' : 'text-green-600';
    },
    benchmarkLabel(actual, national) {
      if (actual == null || national == null) return '';
      const diff = (actual - national).toFixed(1);
      return actual > national ? `+${diff}pp vs.` : `${diff}pp vs.`;
    },
    riskClass(level) {
      if (level === 'HIGH') return 'bg-red-100 text-red-700 border border-red-200';
      if (level === 'MEDIUM') return 'bg-amber-100 text-amber-700 border border-amber-200';
      return 'bg-green-100 text-green-700 border border-green-200';
    },
  },
};
</script>
